import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, categories } from '@/mocks/videos';
import type { VideoItem } from '@/mocks/videos';

export default function Category() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === category) || categories[0];
  const categoryVideos = useMemo(
    () => videos.filter((v) => v.category === category),
    [category],
  );

  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');

  const sortedVideos = useMemo(() => {
    const list = [...categoryVideos];
    if (sortBy === 'rating') return list.sort((a, b) => b.rating - a.rating);
    if (sortBy === 'year') return list.sort((a, b) => b.year - a.year);
    return list;
  }, [categoryVideos, sortBy]);

  const handleCategoryChange = (slug: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/category/${slug}`);
  };

  const handleArticleClick = (id: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/article/${id}`);
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[{ label: currentCategory.name }]}
        />

        {/* Category Header */}
        <section className="w-full bg-background-50 py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary-100 flex items-center justify-center">
                <i className={`${currentCategory.icon} text-2xl md:text-3xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {categoryVideos.length} 部作品
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
                    category === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Sort & Content */}
        <section className="w-full bg-background-100 py-8 md:py-10 min-h-[400px]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                找到 <strong className="text-foreground-800">{categoryVideos.length}</strong> 部{currentCategory.name}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
                {[
                  { value: 'default' as const, label: '默认' },
                  { value: 'rating' as const, label: '评分最高' },
                  { value: 'year' as const, label: '最新' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {sortedVideos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mb-4">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请查看其他分类</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {sortedVideos.map((video: VideoItem) => (
                  <article
                    key={video.id}
                    className="group cursor-pointer rounded-xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300"
                    onClick={() => handleArticleClick(video.id)}
                  >
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={video.image}
                        alt={video.title}
                        title={`${video.title} - 青柠影视${currentCategory.name}高清在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 text-xs rounded-md bg-black/60 text-white whitespace-nowrap">
                          {video.rating}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-1 text-xs rounded-md bg-primary-500 text-background-50 whitespace-nowrap">
                          {video.status}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform scale-75 group-hover:scale-100">
                          <i className="ri-play-fill text-2xl text-background-50 ml-1"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                        {video.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-foreground-400">
                          <span>{video.year}</span>
                          <span>·</span>
                          <span>{video.region}</span>
                        </div>
                        <span className="text-xs text-foreground-400">{video.type}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}